/**
 * View data export — the non-board representations export their DATA, not a board
 * image. A board is a picture (PNG/SVG); a table/matrix/grid is a spreadsheet
 * (CSV); the index is a directory (CSV rows + a printable document). These pure
 * serializers turn each view model into a downloadable artifact; the download
 * seam (`downloadText`) is the only DOM touch.
 *
 * CSV follows RFC 4180: a field containing a comma, quote, CR or LF is wrapped in
 * double quotes with interior quotes doubled. A UTF-8 BOM is prepended at download
 * time so Excel opens non-ASCII correctly.
 */
import { triggerDownload } from "../../board/exportShared.js";
import type { IndexItemVM } from "../indexItems.js";

export type CsvValue = string | number | boolean | null | undefined;

/** One CSV field, RFC-4180 escaped. */
export function csvCell(v: CsvValue): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** A matrix of values → a CRLF-joined CSV string (no trailing newline). */
export function toCsv(rows: readonly (readonly CsvValue[])[]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

// ── Per-representation serializers ─────────────────────────────────────────

/** The table (columns) view → header row of column titles + one row per record. */
export function tableToCsv(model: {
  readonly columns: readonly { readonly title: string }[];
  readonly rows: readonly { readonly cells: readonly CsvValue[] }[];
}): string {
  const header = model.columns.map((c) => c.title);
  return toCsv([header, ...model.rows.map((r) => r.cells)]);
}

/** A grid cross-tab → the row-axis label in col 0, then each column's members
 *  joined by "; ". */
export function gridToCsv(model: {
  readonly axisRows: readonly string[];
  readonly axisCols: readonly string[];
  readonly cells: readonly (readonly { readonly members: readonly { readonly label: string }[] }[])[];
}): string {
  const header: CsvValue[] = ["", ...model.axisCols];
  const rows = model.axisRows.map((r, ri) => [
    r,
    ...model.axisCols.map((_c, ci) => (model.cells[ri]?.[ci]?.members ?? []).map((m) => m.label).join("; ")),
  ]);
  return toCsv([header, ...rows]);
}

/** A matrix adjacency cross-tab → "1" where a cell is present, else blank. */
export function matrixToCsv(model: {
  readonly axisRows: readonly string[];
  readonly axisCols: readonly string[];
  readonly cells: readonly (readonly { readonly present: boolean }[])[];
}): string {
  const header: CsvValue[] = ["", ...model.axisCols];
  const rows = model.axisRows.map((r, ri) => [
    r,
    ...model.axisCols.map((_c, ci) => (model.cells[ri]?.[ci]?.present ? "1" : "")),
  ]);
  return toCsv([header, ...rows]);
}

/** The index → one row per module with its detail facts flattened. */
export function indexToCsv(items: readonly IndexItemVM[]): string {
  const header = ["Name", "Type", "Key", "Description", "Submodules", "Depends on", "Used by", "Interfaces", "Aspects"];
  const rows = items.map((it) => [
    it.name,
    it.typeName,
    it.key,
    it.description ?? "",
    it.childCount,
    it.dependsOn,
    it.usedBy,
    it.interfaces.join("; "),
    it.aspects.map((a) => `${a.key}=${a.value}`).join("; "),
  ]);
  return toCsv([header, ...rows]);
}

// ── Index PDF document ─────────────────────────────────────────────────────

export interface IndexPdfOptions {
  /** Document title on the first page. */
  readonly title?: string;
}

/**
 * Compose the index into a paginated PDF module directory — one block per module
 * (name · type · facts · interfaces · aspects · description), wrapped and
 * page-broken. jsPDF is DYNAMICALLY imported so its ~300KB never enters the main
 * (or viewer) bundle — it loads only when a user actually exports a PDF. Returns
 * the jsPDF doc (the test asserts on it; callers `.output("blob")`).
 */
export async function buildIndexPdf(
  items: readonly IndexItemVM[],
  opts: IndexPdfOptions = {},
): Promise<import("jspdf").jsPDF> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 40;
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - M * 2;
  let y = M;
  const ensure = (h: number): void => {
    if (y + h > pageH - M) {
      doc.addPage();
      y = M;
    }
  };
  const lines = (text: string, size: number): string[] => {
    doc.setFontSize(size);
    return doc.splitTextToSize(text, contentW) as string[];
  };

  doc.setFont("helvetica", "bold").setFontSize(18);
  doc.text(opts.title ?? "Module Index", M, y);
  y += 24;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(120);
  doc.text(`${items.length} modules`, M, y);
  y += 22;
  doc.setTextColor(0);

  for (const it of items) {
    ensure(48);
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(it.name, M, y);
    const nameW = doc.getTextWidth(it.name);
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(130);
    doc.text(it.typeName, M + nameW + 8, y);
    doc.setTextColor(0);
    y += 15;

    const facts: string[] = [];
    if (it.childCount) facts.push(`${it.childCount} submodules`);
    if (it.dependsOn) facts.push(`depends on ${it.dependsOn}`);
    if (it.usedBy) facts.push(`used by ${it.usedBy}`);
    if (it.interfaces.length) facts.push(`exposes ${it.interfaces.join(", ")}`);
    if (it.aspects.length) facts.push(it.aspects.map((a) => `${a.key}: ${a.value}`).join("   "));
    if (facts.length) {
      doc.setTextColor(90);
      for (const line of lines(facts.join("   ·   "), 9)) {
        ensure(12);
        doc.text(line, M, y);
        y += 12;
      }
      doc.setTextColor(0);
    }
    if (it.description) {
      for (const line of lines(it.description, 10)) {
        ensure(13);
        doc.text(line, M, y);
        y += 13;
      }
    }
    y += 8;
    ensure(1);
    doc.setDrawColor(220);
    doc.line(M, y, pageW - M, y);
    y += 14;
  }
  return doc;
}

/** Build + download the index PDF (dynamic jsPDF; nothing loads until called). */
export async function downloadIndexPdf(
  items: readonly IndexItemVM[],
  filename: string,
  title: string,
): Promise<void> {
  const doc = await buildIndexPdf(items, { title });
  triggerDownload(doc.output("blob"), filename);
}

// ── Download seam ──────────────────────────────────────────────────────────

/** Prepend a UTF-8 BOM so Excel/Numbers detect the encoding. */
const BOM = "﻿";

/** Trigger a text-file download (CSV/HTML/Markdown). */
export function downloadText(text: string, filename: string, mime: string): void {
  triggerDownload(new Blob([BOM + text], { type: `${mime};charset=utf-8` }), filename);
}
